/**
 * The mixer: turns replayer channel state into audio.
 *
 * Two things here are deliberately *not* faithful to the Amiga, because they are
 * strictly improvements the hardware could not offer: selectable interpolation
 * above Paula's zero-order hold, and short volume ramps that remove the clicks
 * real hardware produced on every note start. Both are switchable, so the
 * authentic crunch is always one setting away.
 */
import { LOOP_PINGPONG, type Sample } from '../types';
import type { Channel } from '../player/channel';
import { PaulaFilter, softClip, type PaulaModel } from './filters';

export type Interpolation = 'none' | 'linear' | 'cubic' | 'sinc';

export interface MixerSettings {
  interpolation: Interpolation;
  /** 0 = full stereo separation (hard Amiga panning), 1 = mono. */
  stereoSeparation: number;
  paulaModel: PaulaModel;
  /** Master gain multiplier applied on top of the channel-count normalisation. */
  amplification: number;
  /** Volume ramp length in milliseconds. 0 disables ramping (Amiga-authentic). */
  rampMs: number;
  /** Per-channel mute flags. */
  muted: boolean[];
}

export const DEFAULT_MIXER_SETTINGS: MixerSettings = {
  interpolation: 'cubic',
  stereoSeparation: 0.65,
  paulaModel: 'a500',
  amplification: 1.0,
  rampMs: 1.5,
  muted: [],
};

const SINC_TAPS = 8;
const SINC_PHASES = 512;

/**
 * Precomputed 8-tap Blackman-windowed sinc, 512 sub-sample phases.
 *
 * Built once at module load: at 512 phases the table is 4096 floats, which is
 * nothing, and it turns the highest-quality resampler into eight multiplies.
 */
const SINC_TABLE = buildSincTable();

function buildSincTable(): Float32Array {
  const table = new Float32Array(SINC_PHASES * SINC_TAPS);
  const half = SINC_TAPS / 2;
  for (let phase = 0; phase < SINC_PHASES; phase++) {
    const frac = phase / SINC_PHASES;
    let sum = 0;
    for (let tap = 0; tap < SINC_TAPS; tap++) {
      const x = tap - half + 1 - frac;
      let v: number;
      if (Math.abs(x) < 1e-9) {
        v = 1;
      } else {
        const px = Math.PI * x;
        v = Math.sin(px) / px;
      }
      // Blackman window across the tap span.
      const w = (tap + 1 - frac) / (SINC_TAPS + 1);
      const window =
        0.42 - 0.5 * Math.cos(2 * Math.PI * w) + 0.08 * Math.cos(4 * Math.PI * w);
      v *= window;
      table[phase * SINC_TAPS + tap] = v;
      sum += v;
    }
    // Normalise each phase so constant input passes at unity gain.
    if (sum !== 0) {
      for (let tap = 0; tap < SINC_TAPS; tap++) {
        table[phase * SINC_TAPS + tap] /= sum;
      }
    }
  }
  return table;
}

/**
 * Read a sample frame, resolving loop wrap-around.
 *
 * Interpolators need neighbours either side of the read position, and at a loop
 * point the correct neighbour is on the far side of the loop rather than the
 * silence past the end — getting this wrong puts a click on every loop.
 */
function frameAt(s: Sample, index: number): number {
  const len = s.length;
  if (len === 0) return 0;
  let i = index;

  if (s.loopType !== 0 && s.loopLength > 1) {
    const start = s.loopStart;
    const end = s.loopStart + s.loopLength;
    if (i >= end) {
      if (s.loopType === LOOP_PINGPONG) {
        const over = (i - end) % (2 * s.loopLength);
        i = over < s.loopLength ? end - 1 - over : start + (over - s.loopLength);
      } else {
        i = start + ((i - start) % s.loopLength);
      }
    } else if (i < start && i < 0) {
      i = 0;
    }
  }

  if (i < 0) i = 0;
  if (i >= len) i = len - 1;
  return s.data[i];
}

export class Mixer {
  settings: MixerSettings;
  sampleRate: number;

  private filterL: PaulaFilter;
  private filterR: PaulaFilter;

  /** Peak level per channel since the last read, for VU meters. */
  readonly channelPeaks: Float32Array;
  /** Most recent mono output per channel, for oscilloscopes. */
  readonly scopes: Float32Array[];
  private scopeWrite: number[] = [];
  masterPeakL = 0;
  masterPeakR = 0;

  constructor(sampleRate: number, maxChannels = 64, scopeLength = 512, settings?: Partial<MixerSettings>) {
    this.sampleRate = sampleRate;
    this.settings = { ...DEFAULT_MIXER_SETTINGS, ...settings, muted: [] };
    this.filterL = new PaulaFilter(sampleRate);
    this.filterR = new PaulaFilter(sampleRate);
    this.channelPeaks = new Float32Array(maxChannels);
    this.scopes = [];
    for (let i = 0; i < maxChannels; i++) {
      this.scopes.push(new Float32Array(scopeLength));
      this.scopeWrite.push(0);
    }
  }

  setSampleRate(rate: number): void {
    this.sampleRate = rate;
    this.filterL.setSampleRate(rate);
    this.filterR.setSampleRate(rate);
  }

  setLedFilter(on: boolean): void {
    this.filterL.ledEnabled = on;
    this.filterR.ledEnabled = on;
  }

  reset(): void {
    this.filterL.reset();
    this.filterR.reset();
    this.channelPeaks.fill(0);
    for (const s of this.scopes) s.fill(0);
  }

  /**
   * Render `count` frames of the given channels into the output buffers.
   *
   * Called once per tick boundary (or per remaining block within a tick), so
   * channel parameters are constant across the call and gain ramping only has to
   * cover the discontinuity at the start.
   */
  render(
    channels: Channel[],
    outL: Float32Array,
    outR: Float32Array,
    offset: number,
    count: number,
  ): void {
    if (count <= 0) return;

    const { stereoSeparation, amplification, muted } = this.settings;
    this.filterL.model = this.settings.paulaModel;
    this.filterR.model = this.settings.paulaModel;

    // Normalising by channel count keeps a 32-channel XM at a comparable level
    // to a 4-channel MOD rather than clipping into the limiter.
    const master = amplification / Math.sqrt(Math.max(1, channels.length));
    const rampFrames = Math.max(0, Math.round((this.settings.rampMs * this.sampleRate) / 1000));

    for (let i = 0; i < count; i++) {
      outL[offset + i] = 0;
      outR[offset + i] = 0;
    }

    for (let c = 0; c < channels.length; c++) {
      const ch = channels[c];
      if (muted[c]) {
        ch.rampVolumeL = 0;
        ch.rampVolumeR = 0;
        continue;
      }
      this.renderChannel(ch, c, outL, outR, offset, count, stereoSeparation, master, rampFrames);
    }

    // Master stage: Amiga filtering, then a soft limiter.
    let peakL = 0;
    let peakR = 0;
    for (let i = 0; i < count; i++) {
      const l = softClip(this.filterL.process(outL[offset + i]));
      const r = softClip(this.filterR.process(outR[offset + i]));
      outL[offset + i] = l;
      outR[offset + i] = r;
      const al = Math.abs(l);
      const ar = Math.abs(r);
      if (al > peakL) peakL = al;
      if (ar > peakR) peakR = ar;
    }
    if (peakL > this.masterPeakL) this.masterPeakL = peakL;
    if (peakR > this.masterPeakR) this.masterPeakR = peakR;
  }

  private renderChannel(
    ch: Channel,
    index: number,
    outL: Float32Array,
    outR: Float32Array,
    offset: number,
    count: number,
    separation: number,
    master: number,
    rampFrames: number,
  ): void {
    const s = ch.sample;
    if (!ch.active || !s || ch.frequency <= 0) {
      // Ramp a stopping voice down rather than cutting it, or every note-off
      // would click.
      if (ch.rampVolumeL !== 0 || ch.rampVolumeR !== 0) {
        this.rampToSilence(ch, index, outL, outR, offset, count, rampFrames);
      }
      return;
    }

    const step = ch.frequency / this.sampleRate;

    // Constant-power-ish pan, then pulled towards centre by the separation
    // control so hard-panned Amiga modules are listenable on headphones.
    const pan = ch.outPanning;
    const centred = 0.5 + (pan - 0.5) * (1 - separation);
    const targetL = ch.outVolume * master * Math.sqrt(1 - centred);
    const targetR = ch.outVolume * master * Math.sqrt(centred);

    if (!ch.rampInit) {
      ch.rampVolumeL = targetL;
      ch.rampVolumeR = targetR;
      ch.rampInit = true;
    }

    const ramp = Math.min(rampFrames, count);
    const dL = ramp > 0 ? (targetL - ch.rampVolumeL) / ramp : 0;
    const dR = ramp > 0 ? (targetR - ch.rampVolumeR) / ramp : 0;
    let vL = ramp > 0 ? ch.rampVolumeL : targetL;
    let vR = ramp > 0 ? ch.rampVolumeR : targetR;

    const interp = this.settings.interpolation;
    const scope = this.scopes[index];
    let scopePos = this.scopeWrite[index];
    let peak = this.channelPeaks[index];

    for (let i = 0; i < count; i++) {
      if (!ch.playing) break;

      const value = this.sampleValue(s, ch.position, interp);

      if (i < ramp) {
        vL += dL;
        vR += dR;
      } else {
        vL = targetL;
        vR = targetR;
      }

      outL[offset + i] += value * vL;
      outR[offset + i] += value * vR;

      const a = Math.abs(value * ch.outVolume);
      if (a > peak) peak = a;
      scope[scopePos] = value * ch.outVolume;
      scopePos = (scopePos + 1) % scope.length;

      this.advance(ch, s, step);
    }

    ch.rampVolumeL = vL;
    ch.rampVolumeR = vR;
    this.scopeWrite[index] = scopePos;
    this.channelPeaks[index] = peak;
  }

  /** Fade a just-stopped voice out over the ramp window to avoid a click. */
  private rampToSilence(
    ch: Channel,
    index: number,
    outL: Float32Array,
    outR: Float32Array,
    offset: number,
    count: number,
    rampFrames: number,
  ): void {
    const s = ch.sample;
    const ramp = Math.min(rampFrames, count);
    if (ramp <= 0 || !s) {
      ch.rampVolumeL = 0;
      ch.rampVolumeR = 0;
      return;
    }
    const dL = -ch.rampVolumeL / ramp;
    const dR = -ch.rampVolumeR / ramp;
    const step = ch.frequency > 0 ? ch.frequency / this.sampleRate : 0;
    const scope = this.scopes[index];
    let scopePos = this.scopeWrite[index];

    for (let i = 0; i < ramp; i++) {
      const value = this.sampleValue(s, ch.position, this.settings.interpolation);
      ch.rampVolumeL += dL;
      ch.rampVolumeR += dR;
      outL[offset + i] += value * ch.rampVolumeL;
      outR[offset + i] += value * ch.rampVolumeR;
      scope[scopePos] = 0;
      scopePos = (scopePos + 1) % scope.length;
      if (step > 0) ch.position += step;
    }
    ch.rampVolumeL = 0;
    ch.rampVolumeR = 0;
    this.scopeWrite[index] = scopePos;
  }

  private sampleValue(s: Sample, pos: number, interp: Interpolation): number {
    const i = Math.floor(pos);
    const frac = pos - i;

    switch (interp) {
      case 'none':
        // Paula's zero-order hold — the crunchy sound of the real thing.
        return frameAt(s, i);

      case 'linear': {
        const a = frameAt(s, i);
        const b = frameAt(s, i + 1);
        return a + (b - a) * frac;
      }

      case 'cubic': {
        // Catmull-Rom: smooth, cheap, and no overshoot worth worrying about.
        const p0 = frameAt(s, i - 1);
        const p1 = frameAt(s, i);
        const p2 = frameAt(s, i + 1);
        const p3 = frameAt(s, i + 2);
        const a = 2 * p1;
        const b = p2 - p0;
        const c = 2 * p0 - 5 * p1 + 4 * p2 - p3;
        const d = -p0 + 3 * p1 - 3 * p2 + p3;
        return 0.5 * (a + b * frac + c * frac * frac + d * frac * frac * frac);
      }

      case 'sinc':
      default: {
        const phase = (frac * SINC_PHASES) | 0;
        const base = phase * SINC_TAPS;
        const start = i - SINC_TAPS / 2 + 1;
        let acc = 0;
        for (let t = 0; t < SINC_TAPS; t++) {
          acc += frameAt(s, start + t) * SINC_TABLE[base + t];
        }
        return acc;
      }
    }
  }

  /** Advance the read position, honouring forward and ping-pong loops. */
  private advance(ch: Channel, s: Sample, step: number): void {
    ch.position += step * ch.direction;

    if (s.loopType !== 0 && s.loopLength > 1) {
      const start = s.loopStart;
      const end = s.loopStart + s.loopLength;

      if (s.loopType === LOOP_PINGPONG) {
        if (ch.position >= end) {
          ch.position = end - (ch.position - end) - 1e-6;
          ch.direction = -1;
        } else if (ch.position < start) {
          ch.position = start + (start - ch.position);
          ch.direction = 1;
        }
        // A very short loop with a very high step can overshoot both ends.
        if (ch.position < start || ch.position >= end) {
          ch.position = start;
          ch.direction = 1;
        }
      } else if (ch.position >= end) {
        ch.position = start + ((ch.position - start) % s.loopLength);
      }
      return;
    }

    if (ch.position >= s.length) {
      ch.playing = false;
      ch.position = s.length;
    }
  }

  /** Read and clear the VU peaks. */
  takePeaks(out: Float32Array): void {
    const n = Math.min(out.length, this.channelPeaks.length);
    for (let i = 0; i < n; i++) {
      out[i] = this.channelPeaks[i];
      this.channelPeaks[i] = 0;
    }
  }
}
